import { useState } from 'react';
import clsx from 'clsx';
import { SessionSummary } from '../../hooks/useProgress';

export interface SessionListProps {
  sessions: SessionSummary[];
  maxItems?: number;
  showHeader?: boolean;
}

export function SessionList({ sessions, maxItems = 10, showHeader = true }: SessionListProps) {
  // "View all" expands the list in place: every loaded session is already here.
  const [showAll, setShowAll] = useState(false);
  const displaySessions = showAll ? sessions : sessions.slice(0, maxItems);

  if (displaySessions.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
          <MicIcon className="w-8 h-8 text-gray-400" />
        </div>
        <p className="text-gray-500">No sessions yet</p>
        <p className="text-sm text-gray-400 mt-1">Start practicing to see your history here</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      {showHeader && (
        <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-gray-50 text-xs font-medium uppercase tracking-wide text-gray-500 rounded-t-lg">
          <div className="col-span-2">Date</div>
          <div className="col-span-3">Scenario</div>
          <div className="col-span-2">Persona</div>
          <div className="col-span-1 text-center">Clarity</div>
          <div className="col-span-1 text-center">Pace</div>
          <div className="col-span-1 text-center">Conf.</div>
          <div className="col-span-2 text-right">Overall</div>
        </div>
      )}

      <div className="divide-y divide-gray-100">
        {displaySessions.map((session, index) => (
          <SessionRow key={session.id} session={session} index={index} />
        ))}
      </div>

      {sessions.length > maxItems && (
        <div className="px-4 py-3 text-center">
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            aria-expanded={showAll}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            {showAll ? 'Show fewer sessions' : `View all ${sessions.length} sessions`}
          </button>
        </div>
      )}
    </div>
  );
}

interface SessionRowProps {
  session: SessionSummary;
  index: number;
}

function SessionRow({ session, index }: SessionRowProps) {
  const date = new Date(session.date);
  const formattedDate = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  const formattedTime = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });

  return (
    <div
      className={clsx(
        'grid grid-cols-12 gap-4 px-4 py-3 items-center hover:bg-gray-50 transition-colors cursor-pointer',
        index % 2 === 1 && 'bg-gray-50/50'
      )}
    >
      {/* Date */}
      <div className="col-span-2">
        <div className="text-sm font-medium text-gray-900">{formattedDate}</div>
        <div className="text-xs text-gray-500">{formattedTime}</div>
      </div>

      {/* Scenario */}
      <div className="col-span-3">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700">
          {session.scenario}
        </span>
      </div>

      {/* Persona */}
      <div className="col-span-2">
        {session.persona ? (
          <span className="text-sm text-gray-600">{session.persona}</span>
        ) : (
          <span className="text-sm text-gray-400 italic">Free practice</span>
        )}
      </div>

      {/* Clarity */}
      <div className="col-span-1 text-center">
        <ScoreBadge score={session.clarity} />
      </div>

      {/* Pace */}
      <div className="col-span-1 text-center">
        <ScoreBadge score={session.pace} />
      </div>

      {/* Confidence */}
      <div className="col-span-1 text-center">
        <ScoreBadge score={session.confidence} />
      </div>

      {/* Overall */}
      <div className="col-span-2 text-right">
        <span
          className={clsx(
            'inline-flex items-center px-3 py-1 rounded-lg text-sm font-bold',
            session.overallScore === null
              ? 'bg-gray-100 text-gray-500'
              : session.overallScore >= 80
              ? 'bg-green-100 text-green-700'
              : session.overallScore >= 60
              ? 'bg-primary-100 text-primary-700'
              : 'bg-orange-100 text-orange-700'
          )}
        >
          {session.overallScore === null ? 'Not measured' : `${session.overallScore}%`}
        </span>
      </div>
    </div>
  );
}

/** `null` renders as an explicit dash + tooltip, never as 0%. */
function ScoreBadge({ score }: { score: number | null }) {
  if (score === null || score === undefined) {
    return (
      <span className="text-sm text-gray-400" title="Not measured in this session">
        —
      </span>
    );
  }
  return (
    <span
      className={clsx(
        'text-sm font-medium',
        score >= 80 ? 'text-green-600' : score >= 60 ? 'text-gray-700' : 'text-orange-600'
      )}
    >
      {score}%
    </span>
  );
}

// Compact version for mobile or sidebar
export function SessionListCompact({ sessions, maxItems = 5 }: SessionListProps) {
  const displaySessions = sessions.slice(0, maxItems);

  if (displaySessions.length === 0) {
    return (
      <div className="text-center py-6 text-gray-400 text-sm">
        No sessions yet
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {displaySessions.map((session) => (
        <div
          key={session.id}
          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-900 truncate">
                {session.scenario}
              </span>
              {session.persona && (
                <span className="text-xs text-gray-500">
                  w/ {session.persona}
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {new Date(session.date).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              })}
            </div>
          </div>
          <div
            className={clsx(
              'flex-shrink-0 px-3 py-1 rounded-full text-sm font-bold',
              session.overallScore === null
                ? 'bg-gray-100 text-gray-500'
                : session.overallScore >= 80
                ? 'bg-green-100 text-green-700'
                : session.overallScore >= 60
                ? 'bg-primary-100 text-primary-700'
                : 'bg-orange-100 text-orange-700'
            )}
          >
            {session.overallScore === null ? 'Not measured' : `${session.overallScore}%`}
          </div>
        </div>
      ))}
    </div>
  );
}

function MicIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
      />
    </svg>
  );
}
